/**
 * HTTP layer for site groups. Site-group detail and per-customer lists
 * (full/minimal/stats) and scope-search suggestions. The bulk lifecycle
 * actions (deactivate with preflight, reactivate, transfer) are RTK Query
 * mutations in features/entities/siteGroups.js.
 *
 * Contains:
 * - fetchSiteGroups / fetchSiteGroup: reads
 * - fetchSiteGroupsForCustomer / fetchSiteGroupsMinimalForCustomer: per-customer lists
 * - fetchSiteGroupStats: odl-app summary stats for the grid cards
 * - fetchSiteGroupScopeSuggestions: scope-search typeahead
 * - fetchSiteGroupPage / fetchSiteGroupById: the searchable, paged picker surface
 */
// SiteGroup API

import apiClient from '@sophocles/http-common';
import { generateQueryParams } from '@sophocles/utils';

const SITE_GROUP_ENDPOINT = '/sitegroups';

export const fetchSiteGroups = queryParams =>
  apiClient.get(SITE_GROUP_ENDPOINT, { params: generateQueryParams(queryParams) }).then(res => res.data);

export const fetchSiteGroup = siteGroupId =>
  apiClient.get(`${SITE_GROUP_ENDPOINT}/${siteGroupId}`).then(res => res.data);

// Scope-search autocomplete: server-side text_search + pagination, distinct from
// fetchSiteGroups's generateQueryParams-shaped listing.
export const fetchSiteGroupScopeSuggestions = params =>
  apiClient.get(`${SITE_GROUP_ENDPOINT}/`, { params }).then(res => res.data);

// Customer-nested listing used by the user-settings site-group access picker.
export const fetchSiteGroupsForCustomer = customerId =>
  apiClient.get(`/customers/${customerId}/sitegroups/`, { params: { limit: 1000 } }).then(res => res.data);

// Customer-nested minimal listing (id/name/description only) — the dashboard
// filter picker and TransferSitesDialog's destination picker, each with their
// own params shape.
export const fetchSiteGroupsMinimalForCustomer = (customerId, params = {}) =>
  apiClient.get(`/customers/${customerId}/sitegroups/minimal/`, { params }).then(res => res.data);

// odl-app summary stats behind the SiteGroupGrid cards — bare
// {total_groups, alarming_sites, needs_maintenance_sites}, honoring the grid's
// filter[...] params. Distinct base path from the /sitegroups listing above.
export const fetchSiteGroupStats = params =>
  apiClient.get('odl-app/site-groups/stats/', { params }).then(res => res.data);

const ODL_APP_SITE_GROUPS = 'odl-app/site-groups/';

/**
 * One page of a customer's site groups, narrowed by typed text.
 *
 * The picker behind this never holds a customer's whole roster — an operator
 * with thousands of groups would wait on all of them to choose one — so the
 * server does the matching (over name and description) and the reader pages
 * through what it finds.
 *
 * @param {object} query
 * @param {string|number} query.customerId
 * @param {string} [query.search] - typed text; omitted when blank.
 * @param {number} [query.limit]
 * @param {number} [query.offset]
 * @param {object} [config] - axios config; threads an AbortSignal for stale-guarding.
 * @returns {Promise<{ rows: object[], total: number }>}
 */
export const fetchSiteGroupPage = ({ customerId, search, limit = 25, offset = 0 }, config) =>
  apiClient
    .get(ODL_APP_SITE_GROUPS, {
      params: {
        'filter[customer_id]': customerId,
        ...(search ? { 'filter[search]': search } : {}),
        sort: 'name',
        limit,
        offset,
      },
      ...config,
    })
    .then(res => ({ rows: res.data?.data ?? [], total: res.data?.meta?.total ?? 0 }));

/**
 * One site group by id, for naming a selection no loaded page carries.
 *
 * @param {string|number} siteGroupId
 * @param {object} [config] - axios config.
 * @returns {Promise<?object>} the group row, or null when the response is empty.
 */
export const fetchSiteGroupById = (siteGroupId, config) =>
  apiClient.get(`${ODL_APP_SITE_GROUPS}${siteGroupId}/`, config).then(res => res.data?.data ?? null);
